import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { beginChromeTransition, DEFAULT_COMPANY_ID, SIDEBAR_WIDTH, SIDEBAR_MINIMIZED_WIDTH, SIDEBAR_HEADER_HEIGHT, SIDEBAR_ROW_HEIGHT } from 'config'
import { useAuth, useCanAccess, useColorMode, useChecklistVisibility, useInternalRouteAccess, useIsInternalUser, useShortcutBindings } from 'hooks'
import { useCompaniesQuery, useInternalSupportOpenCount, useReferralsEnabled } from 'hooks/api'
import { useCurrentUserCompany } from 'hooks/api/useCompanyQuery'
// Direct subpath: the `components` barrel re-exports navigation, so importing through it
// would be a cycle.
import { openReferABrandModal } from 'components/feedback/modals/ReferABrandModal/openReferABrandModal'
import {
  ReceiptTextIcon,
  BadgeCheckIcon,
  TagIcon,
  HandCoinsIcon,
  ShoppingBagIcon,
  PaletteIcon,
  ChartNoAxesColumnIcon,
  ChartNoAxesCombinedIcon,
  ActivityIcon,
  UsersIcon,
  CircleHelp,
  SettingsIcon,
  BoxIcon,
  StoreIcon,
  ChevronLeftIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  FlaskConical,
  RepeatIcon,
  BlocksIcon,
  LifeBuoyIcon,
  // VRG-221 — the same glyph the Help popover's "What's new" row uses, so the staff
  // authoring surface and the row it feeds read as the same thing.
  MegaphoneIcon,
  // VRG-461 — the market-data corpus is a curated feed of articles/posts.
  NewspaperIcon,
  // VRG-744 — Competitor research: looking out at the field.
  BinocularsIcon,
  HouseIcon,
  RadarIcon,
  // VRG-401 — Signups. A queue you work down, so a checklist rather than a person
  // glyph: UsersIcon is already the Users row, and the distinction being drawn here
  // is "applications awaiting a decision", not "people".
  ClipboardListIcon,
  // VRG-672 — GTM. Channels is a broadcast mast (one row per outbound channel); SEO & AEO
  // is a framed search, kept distinct from the plain SearchIcon the sidebar's own search
  // trigger uses two rows up, so the two are never mistaken for each other.
  RadioTowerIcon,
  ScanSearchIcon,
  // VRG-673 — Trade shows: an admission ticket, the unit a show is bought in.
  TicketIcon,
  MessagesSquareIcon,
  // VRG-674 — Articles: a written page. (NewspaperIcon is already Content, the market-data
  // library, so the two desks never share a glyph.)
  FileTextIcon,
  // VRG-678 — Insiders: a handshake, the deal an Insider strikes on a brand's behalf. Not the
  // gift (that is Refer & earn, the brand-side row), so the two programmes never share a glyph.
  HandshakeIcon,
  // VRG-655 — "Refer & earn". The same glyph Settings → Refer & earn uses (§21), and
  // navIconMotion already animates it (the lid lifts).
  GiftIcon,
} from 'lucide-react'

import { COMPETITOR_RESEARCH_TITLE, MARKET_RESEARCH_TITLE, ROUTES } from 'routes'
import { Box, Divider, IconButton, Stack, Typography } from '@mui/material'
import MuiDrawer from '@mui/material/Drawer'
import { styled, Theme, CSSObject, useTheme } from '@mui/material/styles'
import logo from 'assets/images/logo.svg'
import vlogo from 'assets/images/vlogo.svg'
import {
  SidebarUserBlock,
  SidebarList,
  SidebarHelpPopover,
  SidebarCollapseToggle,
  SidebarSearchTrigger,
  EncodingDeviceSidebarButton,
} from 'components/navigation/Sidebar/components'
import { theme } from '../../../settings/mui/theme'
import { Button } from '@repo/ui'
import { useAskStore } from 'ask'
import { useVeraSpaceStore } from 'stores'
import { useSearchStore } from 'search'
import { eventMatchesBinding, isTypingTarget } from 'utils'
import {
  readSidebarDisplayMode,
  readSidebarHoverOpen,
  writeSidebarDisplayMode,
  writeSidebarHoverOpen,
  type SidebarDisplayMode,
} from './displayMode'
import { vhMinus } from 'utils/textZoom'
import { sidebarSurface } from './sidebarSurface'
import { NAV_ROW_LIST_ITEM, sidebarIconMotionSx } from '../navIconMotion'
import { selectedNavIconSx } from '../selectedNavIcon'
import { MAIN_NAV_ORDER } from './mainNavOrder'

/**
 * Icons for `MAIN_NAV_ORDER`'s titles, kept separate from the order itself (VRG-227) so
 * the docs index can import the order alone without pulling lucide glyphs it never
 * renders.
 */
const MAIN_NAV_ICONS: Record<string, React.ReactNode> = {
  Home: <HouseIcon strokeWidth={1} />,
  Analytics: <ChartNoAxesColumnIcon strokeWidth={1} />,
  Tags: <TagIcon strokeWidth={1} />,
  Products: <ShoppingBagIcon strokeWidth={1} />,
  Certificates: <BadgeCheckIcon strokeWidth={1} />,
  Sales: <HandCoinsIcon strokeWidth={1} />,
  People: <UsersIcon strokeWidth={1} />,
  Studio: <PaletteIcon strokeWidth={1} />,
}


/**
 * The sidebar's surface.
 *
 * LIGHT: #fafafa — a step above the tinted page (#f4f5f5), a hair below the white surfaces.
 *
 * It was `primary[50]` (#f6f6f6), which against the page measured 1.01:1: the nav and the
 * page were the same colour, so the sidebar read as a strip of background with icons on it.
 * It was then pure white, which fixed that but collapsed it into the popovers that OVERLAY
 * it — a menu opening over the sidebar had only its shadow to distinguish it.
 *
 * So the sidebar sits between: page → sidebar → white surfaces and popovers. That mirrors
 * dark mode, which has three distinct chrome levels (page #09090b, popover #0f0f12, sidebar
 * #18181b), and it is the most a light theme can do with fills — the whole page-to-white
 * range is 1.092:1, so a third fill step is worth ~1.045 and the rest of the elevation cue
 * has to come from the popover's shadow (see the MuiMenu override).
 *
 * DARK: unchanged. There `primary[50]` (#18181b) is a genuine step above the near-black
 * page, so the sidebar already reads as a surface.
 *
 * One helper rather than three literals: this colour is set in three places (expanded rail,
 * collapsed rail, footer block) and they were already the same value by coincidence rather
 * than by construction.
 */

const filters = {
  limit: 100,
  offset: 0,
}

const openedMixin = (theme: Theme): CSSObject => ({
  width: SIDEBAR_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  background: sidebarSurface(theme),
  borderRadius: '0 16px 16px 0',
})

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: SIDEBAR_MINIMIZED_WIDTH,
  background: sidebarSurface(theme),
  borderRadius: '0 16px 16px 0',
  // [theme.breakpoints.up('sm')]: {
  //   width: `calc(${theme.spacing(8)} + 1px)`,
  // },
})

const Drawer = styled(MuiDrawer, { shouldForwardProp: prop => prop !== 'open' })(({ theme }) => ({
  width: SIDEBAR_WIDTH,
  minWidth: SIDEBAR_MINIMIZED_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  // bgcolor: theme.palette.primary[50],
  // Zoom-safe: a plain 100vh here painted z x 100vh and pushed a scrollbar onto
  // the whole document at any scale above Default (measured: 1565px tall in a
  // 1304px window at Extra large). The drawer is the app's only always-on-screen
  // full-height box, so this was THE overflow.
  minHeight: vhMinus(),
  // The sidebar must always sit above page content/chrome (e.g. the Studio save
  // bar) — but below modals/overlays. Pin it to the drawer layer so page elements
  // can't cover it.
  zIndex: theme.zIndex.drawer,
  '& .MuiDrawer-paper': { zIndex: theme.zIndex.drawer },
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    },
  ],
}))

const DrawerHeader = styled('div')(({ theme }) => ({
  minHeight: SIDEBAR_HEADER_HEIGHT,
  height: SIDEBAR_HEADER_HEIGHT,
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0, 3),
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
}))

export const Sidebar = () => {
  // The display mode is the sidebar's own preference (auto | expanded | collapsed).
  // `hoverOpen` tracks the pointer and is what mode `auto` — and only `auto` — renders
  // from.
  //
  // It is updated in EVERY mode, not just auto, so it always describes where the
  // pointer actually is. That matters at exactly one moment: choosing "Auto" from the
  // profile menu necessarily happens with the pointer over the sidebar, so a stale
  // `false` would collapse the sidebar out from under the cursor and leave it that way
  // until the user moved out and back in.
  const [displayMode, setDisplayMode] = useState<SidebarDisplayMode>(readSidebarDisplayMode)
  const [hoverOpen, setHoverOpen] = useState<boolean>(() => readSidebarHoverOpen(true))

  const open = displayMode === 'auto' ? hoverOpen : displayMode === 'expanded'

  const { isAuthenticated } = useAuth()
  // The user's live bindings. The handler below matches against this rather than a
  // constant, so rebinding in Settings takes effect without a reload.
  const { sidebarToggle } = useShortcutBindings()
  // VRG-410 — `isAdmin` (`useCanAccess({ role: 'platform_admin' })`) is GONE from this
  // component. Its last three uses were the console branch, the "Internal" chip and the
  // Labs row, all of which now ask `canReachInternalPath`; it survived only in a
  // dependency array that its own memo never read. That also retires the TODO it carried
  // ("inherit ROLE and apply accordingly"): the answer turned out not to be a role at
  // all, it is per-page permissions, and they live in one table now.
  //
  // VRG-410 — which internal PAGES this staffer can open, from the one route→permission
  // table the URL guard reads (`routes/internalAccess.ts`). The menu and `InternalRoute`
  // must not be able to disagree: a row that leads to a redirect is worse than no row.
  const { isInternal: isInternalStaff, canReachInternalPath } = useInternalRouteAccess()
  // VRG-338: Home sits above Analytics and is PERMANENT — it no longer removes itself, so
  // this entry is unconditional. What retires is the setup checklist, which only affects the
  // progress BADGE below. `checklist` rides along for that; the queries behind it are already
  // running here, so the count costs nothing extra.
  const { isChecklistLive, checklist: homeChecklist } = useChecklistVisibility()
  // VRG-395 — Vera staff (admin OR manager), and not while in a company's space. Gates
  // the Settings gear below; see BOTTOM_MENU.
  const isInternal = useIsInternalUser()
  // VRG-410 — the four per-row permission lookups that used to sit here (Orders on
  // `internal_order_view`, Support on `internal_support_manage`, Announcements on
  // `internal_announcements_manage`, Demos on `demo_provision`) are GONE, replaced by
  // `canReachInternalPath` above. They were correct individually and still produced the
  // defect this ticket exists for: each row decided its own gate, the URL guard decided
  // separately, and the eight rows nobody had written a lookup for were open to any
  // internal role regardless of whether their pages could load. One table, both readers.
  //
  // NOTE: `canViewInventory` used to sit here and was dropped in an earlier merge, not
  // by accident — VERA-455 removed the standalone Inventory sidebar entry on main
  // (Inventory is now Tags filtered by possession), so the permission lookup that
  // fed it is dead. Keeping it would have left an unused gate for a nav item that
  // no longer exists.
  const { resolvedMode } = useColorMode()
  const isDark = resolvedMode === 'dark'
  // VERA-876 corner fix: the drawer's top-right corner is normally rounded to
  // soften against the page background — but the VeraSpaceBanner butts a solid
  // rectangle right up against it, and the curve exposes a notch of page
  // background behind the corner instead of blending. Square it off while the
  // banner is showing so the two edges meet flush.
  const spaceActive = useVeraSpaceStore((s) => !!s.companyId)
  // VRG-227: the Ask panel's fullscreen takeover butts flush against the sidebar's
  // FULL HEIGHT (not just the top, like the banner above) — square off both right
  // corners so the curve does not expose a notch of page background at either end.
  const askExpanded = useAskStore((s) => s.open && s.expanded)
  // Use the dynamic (mode-aware) theme for all render-level palette reads, not the
  // static light-theme import — so sidebar surfaces flip in dark mode.
  const theme = useTheme()

  /**
   * ── TELL THE REST OF THE PAGE THE WIDTH IS ABOUT TO MOVE (VRG-512) ─────────────────
   *
   * `openedMixin`/`closedMixin` above transition `width`. Everything downstream — the
   * analytics canvas, its charts, the scan map — used to find out only by OBSERVING the
   * resulting box, which is structurally a frame late, and each of them then eased its
   * own catch-up on top of this transition. Two animations, the second one starting
   * late against a target that moves again next frame, is the "everything below the
   * toolbar moves slower than the sidebar" report.
   *
   * ⚠️ `useLayoutEffect`, NOT `useEffect`. The point is to be in force for the FIRST
   * frame; a passive effect lands after the commit that already painted it, which is
   * exactly the one-render lateness this replaces (see `appChromeTransition`, and
   * `AnalyticsCanvas`'s `containerResizing`, which had it and is why the lag read as
   * "it drags at the start and then catches up").
   *
   * The duration is read from the theme rather than restated, and the two directions
   * are genuinely different lengths — MUI's `enteringScreen` is 225ms, `leavingScreen`
   * 195ms — so the announced window matches the transition actually running.
   *
   * Skips the first pass: mounting is not a transition, and announcing one would
   * suppress card animation on every page load for nothing.
   */
  const announcedOpenRef = useRef<boolean | null>(null)
  useLayoutEffect(() => {
    if (announcedOpenRef.current === null) {
      announcedOpenRef.current = open
      return
    }
    if (announcedOpenRef.current === open) return
    announcedOpenRef.current = open
    beginChromeTransition(
      open
        ? theme.transitions.duration.enteringScreen
        : theme.transitions.duration.leavingScreen,
    )
  }, [open, theme])

  // Search is NOT a nav item — it opens an overlay rather than routing, so it
  // lives above the list as its own trigger (SidebarSearchTrigger). The
  // commented-out '/search' MAIN_MENU row it replaces is deleted here.
  //
  // One handler for both entry points: the sidebar trigger and the Help
  // popover's "Search help" row. That row is the discovery bridge between Help
  // and search, so it must not drift onto a second implementation.
  //
  // VRG-227: this now opens the real palette. The overlay itself is mounted once in
  // Layout and reached through the store, so the sidebar never holds any search state.
  //
  // Wrapped rather than passed straight through: `openSearch` takes an optional seed
  // query, and SidebarSearchTrigger binds this to `onClick` directly — so handing over
  // the raw action would seed the palette with a React MouseEvent. The prop is typed
  // `() => void`, which does not catch that.
  const openSearch = useSearchStore(s => s.openSearch)
  const handleOpenSearch = useCallback(() => openSearch(), [openSearch])

  // VRG-227: built from `MAIN_NAV_ORDER` (title + link only) rather than a literal array
  // here, so the docs index can read the SAME order without a second, hand-typed copy
  // that could drift out of sync with the rail. See mainNavOrder.ts.
  //
  // VRG-338: Home is FIRST-RUN scaffolding and inserts ABOVE Analytics, so Analytics
  // never permanently loses its slot and there is no reshuffle to undo when Home
  // retires. `showHome` goes false once the setup checklist completes or the user
  // dismisses it — the entry then disappears, which is the intended end state, not a bug.
  // The badge carries setup progress ("2/6") because every step is completed on some
  // OTHER page — this is the only always-visible place the count can live. It disappears
  // once the checklist retires; the ROW does not, because Home is permanent now.
  //
  // Withheld while the six queries settle so the row cannot flash a wrong number on a cold
  // load, and omitted for someone with no checklist at all (a non-admin), for whom "0/6"
  // would be both wrong and un-actionable.
  //
  // VERA-455: no standalone Inventory entry. Inventory is tags filtered by possession —
  // a facet of Tags, not an object the user operates on independently (§8) — so it lives
  // as the Tags > Inventory tab, reachable through the Tags entry.
  //
  // VERA-914 / VRG-105 (Stage B): the "People" surface at /people (Owners | Leads
  // tabs — see PeoplePage). ROUTES.OWNERS now resolves to /people.
  const MAIN_MENU = useMemo(
    () =>
      MAIN_NAV_ORDER.map(({ title, link }) => ({
        title,
        link,
        icon: MAIN_NAV_ICONS[title],
        count:
          title === 'Home' &&
          isChecklistLive &&
          homeChecklist &&
          !homeChecklist.isLoading &&
          homeChecklist.total > 0
            ? `${homeChecklist.completedCount}/${homeChecklist.total}`
            : undefined,
      })),
    [isChecklistLive, homeChecklist]
  )

  // VRG-672 (Spencer, 2026-10-06) — LABS LEFT THIS LIST for the labelled "Product" group at
  // the foot of INTERNAL_MENU; see the IA block below for why. What remains is the
  // company-side Settings gear, so for Vera staff (no gear, VRG-395) this list is EMPTY and
  // is not rendered at all — see the render below, which skips it rather than drawing an
  // empty <List> whose 4px top padding would sit between Help and the divider.
  //
  // DELIBERATELY UNLABELLED (VRG-401), unlike the groups in INTERNAL_MENU. A
  // "TOOLS" heading over a single row reads worse than the row alone: a group label
  // exists to tell a group apart from its neighbours, and this list's neighbours are
  // Help and the account chip rather than another labelled group. It is also in a
  // different component from the internal nav — the bottom block, below the nav list's
  // own scroll boundary — so a heading here would be labelling across a hard edge that
  // already separates it. Same reasoning the settings nav uses to hide `PERSONAL` when
  // it is the sole section (see SettingsPage's `soleSection`).
  // VRG-655 — "Refer & earn" is for BRAND members only: not Vera staff (no company to
  // refer from), not staff inside a brand's space (the API refuses sends there, and the
  // space grant does not carry `referrals_send`), and not on a demo company (a prospect's
  // sandbox cannot refer anyone). Every brand role holds `referrals_send` by default; an
  // admin who turns it off removes the row, because a whole surface the user can never use
  // is hidden rather than disabled (§27).
  //
  // VRG-680 — and only while the API's launch switch is on (`GET /features`). Off is the
  // default everywhere, and it fails closed: no row until the API has said yes.
  const { enabled: referralsLive } = useReferralsEnabled()
  const { isHasAccess: canSendReferrals } = useCanAccess({ action: 'referrals_send' })
  const { data: currentCompany } = useCurrentUserCompany({ enabled: !isInternal && canSendReferrals && referralsLive })
  const showReferABrand =
    referralsLive && !isInternal && !spaceActive && canSendReferrals && currentCompany?.kind !== 'demo'

  const BOTTOM_MENU = useMemo(
    () => [
      // VRG-395: no Settings gear for internal Vera staff. They have no company, so the
      // gear led to a brand's settings that is not theirs; their four personal sections
      // are the "Account" page, reached from the account chip below. Internal-only
      // configuration already lives at /internal/labs, so nothing is orphaned.
      // VRG-655 — an ACTION row: it opens the "Refer & earn" modal rather than routing
      // (SidebarList renders a `<button>` for a row with `onClick` and no `link`). Sits
      // directly above Settings, whose Personal tab holds the full referrals page.
      ...(showReferABrand
        ? [{ title: 'Refer & earn', onClick: openReferABrandModal, icon: <GiftIcon strokeWidth={1} /> }]
        : []),
      ...(isInternal
        ? []
        : [{ title: 'Settings', link: ROUTES.SETTINGS, icon: <SettingsIcon strokeWidth={1} /> }]),
    ],
    [isInternal, showReferABrand]
  )

  /*
    THE INTERNAL NAV'S INFORMATION ARCHITECTURE (VRG-401).

    Six groups, in this order (VRG-672 added GTM, 2026-10-06; VRG-684 renamed Analytics and
    Tools, 2026-10-08; VRG-685 moved Analytics and GTM to the top, 2026-10-08):

      Website · Product                                analytics  (read-only monitoring;
                                                                   was "Insights")
      Channels · SEO & AEO · Articles · Sales          gtm        (how Vera FINDS and WINS
        · Trade shows · Affiliates                                 customers)
      People · Demos · Companies · Subscriptions       accounts   (signup -> customer)
      Tags · Packages · Orders · Invoices              commerce   (goods -> transaction)
      Team · Integrations · Support · Announcements    operations (surfaces staff RUN
        · Content                                                  inside Vera)
      Labs                                             tools      (was "Product")

    SPENCER'S ORDER, 2026-10-08 (recorded in VRG-685): ANALYTICS FIRST, GTM SECOND, ACCOUNTS
    THIRD, then Commerce, Operations and Tools in their old relative order; rows inside each
    group unchanged. This OVERRIDES the lifecycle argument below (operated groups first,
    "Insights sits last", "Companies leads deliberately"), and the reasons those notes gave for
    waiting have since come true. They said Analytics should not lead while it was an
    EmptyState, and to reopen "the day it becomes a real dashboard": since VRG-640 Analytics →
    Website is the live vera.inc funnel, and since VRG-661 it is the post-auth landing for
    internal staff, so it is the daily start point and leading with it puts the first row
    where the day begins. GTM second because it is where Spencer works: the channels, articles
    (publishing lives there since VRG-685), sales and outreach are the desks used most. "Which
    group leads" was the open call the GTM and COMPANIES notes below deferred to Spencer; this
    is that call. Two costs, accepted: a role that reaches no row in either group starts at
    Accounts, because a group with no reachable rows draws no heading (see the filter below)
    — platform_manager does exactly that, since Spencer also decided (same day) that managers
    do not see the Analytics section at all for now (Product's Ask path through
    `internal_support_manage` was removed, route and API); and lifecycle order now holds
    WITHIN Accounts and Commerce only, not across the whole rail. The notes below are kept for
    their reasoning; where they say "last", "between Operations and Insights" or "leads", read
    them as the order before this decision.

    SPENCER'S DECISION, 2026-10-08 (VRG-684). The read-only group is called ANALYTICS, and its
    rows name WHAT is measured: **Website** (`/internal/analytics/website`, the vera.inc funnel,
    sources, map, visitors and leads; was the "Analytics" row at `/internal/analytics`) and
    **Product** (`/internal/analytics/product`, Tours adoption + Ask; was the "Usage" row at
    `/internal/usage`). A row named "Analytics" under a heading that would have said the same was
    the redundancy this removes, and "Usage" said less than "Product" about which product. A third
    row, **Revenue** (finance: ROI by company, Vera's sales over time), is expected here when that
    work lands — it is the commercial half the old "Analytics" row was always meant to hold.
    The group holding Labs is TOOLS, not "Product": with Analytics → Product a row, a heading of
    the same word would have read as its parent. Old addresses redirect in one hop
    (`analyticsIndexTarget`, `legacyUsageTarget` in routes/gtmRedirects.ts). Every gate is
    unchanged by that rename: Website `internal_analytics_view`; Product `anyPermission` with
    per-tab re-checks, so platform_manager opened it to Ask alone — until VRG-685 put both rows
    on `internal_analytics_view` (Spencer: managers do not see Analytics, for now). The notes below predate the rename and say
    "Insights", "Usage" and "Product group" for what are now Analytics, Analytics → Product and
    Tools.

    LABS MOVED INTO A "PRODUCT" GROUP (Spencer's decision, 2026-10-06). It used to sit in the
    sticky bottom block beside the Settings gear, unlabelled, which made the one internal
    tool there read as account chrome — the same strip as Help and the account chip — rather
    than as a page of the console. As the last row of the scrolling list it is a destination
    like every other, under a label that says what it is about: the PRODUCT itself (its
    component gallery, its emails, its experiments), as against customers, goods, staff
    desks, channels or monitoring. Route, icon, gate (`platform_admin` via the access table)
    and active state are unchanged; only where the row is drawn moved.

    PRODUCT SITS LAST, after Insights. Every group above is about running the business
    (accounts, commerce, operations, GTM, monitoring); Product is about building the thing
    being sold, and it is the least visited, so it takes the end of the list. It is also
    the position nearest where Labs used to be — directly above the bottom boundary — so a
    staffer's hand goes to roughly the same place it always did. INSIGHTS SITS LAST below
    was written before this group and now says "of the business groups".

    One row is a thin group, accepted for the same reason Insights' two rows are: the label
    buys a category distinction. Usage (product telemetry) and Announcements (the What's
    new desk) are candidates to join it — NOT moved, because that is Spencer's call.

    GTM IS ITS OWN GROUP, NOT A ROW OF OPERATIONS OR INSIGHTS (Spencer's IA, 2026-10-06).
    Operations is the desks staff run INSIDE Vera — the team, the support queue, the
    announcements feed. GTM is the desks pointed OUTWARD: the channels through which brands
    hear of Vera and are won. "GTM", not "Marketing", because it covers sales and
    partnerships too (Sales, Partnerships, Affiliates are three of its nineteen
    channels), and it mirrors the umbrella repo's `gtm/` canon, so the folder a strategy
    doc lives in and the sidebar group its dashboard lives in carry one name. Two other
    names were taken: "Content" is the Operations row for the market-data library, and
    "Insights" is the read-only group below.

    ONLY CHANNELS VERA OPERATES ON EARN A ROW. Nineteen channel rows would triple a rail
    that already overflows; most channels are Planned or Not now, and a row for one would
    lead to a page with nothing to do on it. So the group is `Channels` — the overview of
    all nineteen, every status visible, the place "is anyone doing trade shows?" gets
    answered — plus one row per channel that has a working surface: SEO & AEO, Articles
    (VRG-674) and Trade shows (VRG-673). Sales and Insiders are planned rows; each is added with
    its page, never before it (a row that leads to an empty state is the
    "promote the least-finished surface" cost the INSIGHTS-SITS-LAST note below rejects).

    SEO & AEO MOVED OUT OF ANALYTICS for the same reason Usage moved out of it: subject.
    Analytics is how the business performs; SEO & AEO is how vera.inc is found, and its
    Recommendations queue is approved and rejected, so it is a desk someone works rather
    than a figure someone reads. `/internal/analytics?tab=seo` redirects to
    `/internal/gtm/seo` with its params, because the review emails already sent link there.

    GTM SITS BETWEEN OPERATIONS AND INSIGHTS. It is an OPERATED group, so it belongs with
    the operated groups above the read-only one, and Insights keeps its last place for the
    reason given below. Within the operated groups, lifecycle would put GTM FIRST — a brand
    is found before it signs up — and that was considered and NOT taken: it would change
    which group leads the nav, and what leads is already an open call for Spencer (see
    COMPANIES LEADS DELIBERATELY). Directly under Operations also puts it next to Content,
    the market-data desk whose own row comment calls it "GTM signal". Worth reopening with
    that call.

    Both of the first two group orders are LIFECYCLE orders, and that is main's call, not
    this branch's: an account moves signup -> demo -> customer -> billing, and goods move
    tag -> package -> order -> invoice. An earlier draft here led Accounts with Companies
    (it is the post-auth landing) — rebutted on merge, because the sidebar already marks
    where you are with the SELECTED ROW, so first position was never carrying that meaning.

    INSIGHTS WAS SPLIT OUT OF OPERATIONS because Operations held two different kinds of
    thing, and its own label admitted it. Every other group label here is defended as being
    TRUE OF EVERY ROW — that is the stated bar ("'Accounts' is true of all four rows"). The
    Operations label could not clear it without an "and": things staff run, *and* Analytics
    is how they see whether any of it is working. Users, Integrations, Support and
    Announcements are surfaces you OPERATE; Analytics and Ask stats are read-only monitoring
    you READ. Six rows was the symptom; two categories under one heading was the cause.

    ANALYTICS AND USAGE SPLIT ON SUBJECT, and that is a second cut after Insights itself.
    Analytics is the COMMERCIAL page — ROI by company, Vera's own sales over time, read by
    leadership on a monthly trend. Usage is PRODUCT TELEMETRY — Tours adoption and Ask
    questions, read by product and support when they ship or triage something. Tours used
    to be a tab of Analytics and Ask its own row here; both are now tabs of Usage.

    The permission asymmetry survives that move rather than blocking it, which was the
    thing to get right. `internal_analytics_view` is platform_admin ONLY;
    `internal_support_manage` (Ask's gate, because its log stores verbatim brand
    questions) is platform_admin AND platform_manager. A single-permission Usage route
    would have to pick one and either lock platform_manager out of a tab it is granted or
    widen who can read those questions. So the route takes `anyPermission`
    (`routes/internalAccess.ts`) and each TAB re-checks: platform_manager opens Usage to
    Ask alone. No permission changed and nobody can see more than before. [SUPERSEDED
    2026-10-08, VRG-685: Spencer chose the "lock platform_manager out" option for now. Both
    tabs and the Ask API take `internal_analytics_view`; managers keep the Support queue.]

    INSIGHTS SITS LAST (of the business groups — Product follows it since VRG-672), not first. Analytics-above-Accounts was considered and dropped:
    `/internal/analytics` is an EmptyState until the ROI work lands, so leading with it
    would promote the least-finished surface; and it is platform_admin-only, so for every
    other internal role the nav would begin with Accounts regardless. Worth reopening the
    day it becomes a real dashboard.

    The first two groups are ordered by LIFECYCLE: an account moves down the first list as
    it becomes real, and goods move down the second as they are boxed, sold and billed. That
    is one rule a reader can hold, and it is what lets someone find a row by asking "how far
    along is this" instead of memorising a sequence.

    It replaced an order with no organising principle at all — Tags, Packages, Orders,
    Invoices, Companies, Demos, Subscriptions, Users… — where a staffer looking for
    Subscriptions had to scan past four fulfilment rows to find it next to Users.

    COMPANIES LEADS DELIBERATELY. It is the hub the other views drill into, and it was the
    post-auth landing for internal users until VRG-661 (2026-10-05) moved that to
    Analytics "for now" (`routes/postAuthLanding.ts`). Companies remains the landing for
    any internal role that cannot open Analytics. The nav order was NOT changed with the
    landing; whether Analytics should lead is an open call for Spencer. Do not "tidy"
    Companies further down the list without that call.

    LABELLED GROUPS, VIA `section` — and the boundary renders as a HEADING when the rail is
    expanded and as a HAIRLINE when it is collapsed. One boundary, two renderings, each
    legible in exactly the state the other is not. See the `section` docblock on `linksType`.

    An earlier pass here shipped hairlines only, on the grounds that four settings-density
    headings cost ~118px in a rail that is 694px of rows in a 249px box at 1280x720. That
    objection did not survive VRG-398 landing on main: the nav list now scrolls properly, so
    the cost is content moving below a fold that works rather than content becoming
    unreachable. With 12 rows the grouping is inferable but not obvious — "Subscriptions is
    an accounts thing, Invoices is a commerce thing" is precisely what a label settles — so
    the labels are worth their height now in a way they were not before.

    The height is bought back by using `NavSectionLabel`'s COMPACT density rather than the
    settings nav's, and by dropping the hairline wherever a heading renders.
  */
  /*
    VRG-410 — THE ROWS ARE DECLARED IN GROUPS AND FILTERED ONCE, rather than each row
    carrying its own `...(canDoX ? [row] : [])` spread.

    Two things forced the restructure, and the second is the one that matters:

    1. EVERY row is now gated, on the same table `InternalRoute` reads. Four rows used to
       be gated and eight were not, so a role could see Packages, Tags, Users, Invoices
       and Subscriptions regardless of whether any of them would load. Inlining twelve
       spreads would have been unreadable, and would have reintroduced the per-row
       decision this ticket is removing.

    2. THE GROUP LABEL HAS TO RIDE ON WHICHEVER ROW SURVIVES. `section` draws a heading
       (expanded) or a hairline (collapsed) ABOVE its row, so attaching it to a fixed row
       means the group loses its label and its top edge for exactly the users whose menu
       is shortest. The old code hand-patched this for ONE case — Commerce, whose leading
       Orders row is `internal_order_view`-gated — with a ternary that moved `section` to
       Invoices. With every row gated, that patch would need to exist per group and per
       row. Deriving it after the filter makes it structural instead: attach the label to
       the first VISIBLE member, or drop the group entirely if none survive.
  */
  // VRG-721 — open-request badge on the Support row. Gated on the SAME reachability
  // check that decides whether the row exists, so a role that cannot open the queue
  // never polls a count it would be refused.
  const canSeeSupportQueue = isInternalStaff && canReachInternalPath(ROUTES.INTERNAL_SUPPORT)
  const { data: openSupportCount } = useInternalSupportOpenCount(canSeeSupportQueue)

  const INTERNAL_MENU = useMemo(() => {
    const groups: Array<{
      section: string
      rows: Array<{ title: string; link: string; icon: React.ReactNode; count?: string }>
    }> = [
      {
        // Read-only monitoring — what staff LOOK AT, as against the surfaces below that they
        // operate. FIRST since 2026-10-08 (Spencer; see SPENCER'S ORDER in the IA block). Two rows is a thin group, and that is accepted deliberately: the heading is
        // buying a category distinction Operations could not state without an "and" (see the
        // IA block above), not merely shortening a list.
        //
        // Both rows take `internal_analytics_view` (platform_admin only) since VRG-685: Spencer,
        // 2026-10-08, platform_manager does not see this section at all, for now (§27: hide a
        // surface a role cannot reach). `section` rides on the first survivor, so with neither
        // row reachable the heading and hairline go too. Until VRG-685 Product was also reachable
        // through `internal_support_manage` for its Ask tab.
        section: 'Analytics',
        rows: [
          // VRG-684 — WEBSITE: the vera.inc funnel (VRG-640). Gated on `internal_analytics_view`,
          // seeded to platform_admin only. Was the "Analytics" row at /internal/analytics.
          { title: 'Website', link: ROUTES.INTERNAL_ANALYTICS_WEBSITE, icon: <ChartNoAxesColumnIcon strokeWidth={1} /> },
          // VRG-684 — PRODUCT: telemetry, Tours + Ask as two tabs of one page (was "Usage",
          // /internal/usage). Two TABS rather than two rows because this rail already overflows.
          // One permission for both since VRG-685 (`internal_analytics_view`; Ask used to take
          // `internal_support_manage` too, which let platform_manager in). A Revenue row
          // (finance, sales over time) is expected here.
          { title: 'Product', link: ROUTES.INTERNAL_ANALYTICS_PRODUCT, icon: <ActivityIcon strokeWidth={1} /> },
        ],
      },
      {
        // VRG-672 — "GTM" is true of every row: each is a go-to-market channel, or the map of
        // all of them. See the IA block above for why it is its own group, why it sits second
        // (Spencer, 2026-10-08: it is where he works), and why only operated channels get a row. Every row is gated on `internal_gtm_view`
        // ("View GTM", VRG-684; it borrowed `internal_analytics_view` until then), through the
        // one `/internal/gtm` entry in routes/internalAccess.ts.
        //
        // Spencer's IA (2026-10-08): Channels, SEO & AEO, Articles, Sales, Trade shows,
        // Affiliates. People stays in Accounts (above) with Demos, Companies and Subscriptions.
        section: 'GTM',
        rows: [
          // The overview, first: it is the group's index, and the only row that answers for
          // the seventeen channels without a page of their own.
          { title: 'Channels', link: ROUTES.INTERNAL_GTM, icon: <RadioTowerIcon strokeWidth={1} /> },
          { title: 'SEO & AEO', link: ROUTES.INTERNAL_GTM_SEO, icon: <ScanSearchIcon strokeWidth={1} /> },
          // VRG-674 — after SEO & AEO, which is the order of Spencer's IA and of the work:
          // SEO finds the terms, an article is what ranks for them.
          // VRG-751 (Spencer, 2026-10-23) — the row is channel #8, "Insights" (it was "Content
          // marketing"): Articles · Ideas · Interviews · Analytics as path tabs under
          // /internal/gtm/insights, so longest-match selection keeps it lit on all four. The name
          // matches the website section it publishes to (`/insights`).
          { title: 'Insights', link: ROUTES.INTERNAL_GTM_INSIGHTS, icon: <FileTextIcon strokeWidth={1} /> },
          // VRG-675 + VRG-684 — Traction channel #13. Outreach is PART of Sales (Spencer,
          // 2026-10-08), so its campaigns are a tab of this page (Campaigns since VRG-713), not a
          // row of its own; a campaign opens under /internal/gtm/sales/campaigns/:id and
          // keeps this row selected (longest-match selection, SidebarList).
          { title: 'Sales', link: ROUTES.INTERNAL_GTM_SALES, icon: <ChartNoAxesCombinedIcon strokeWidth={1} /> },
          // VRG-673.
          { title: 'Trade shows', link: ROUTES.INTERNAL_GTM_TRADE_SHOWS, icon: <TicketIcon strokeWidth={1} /> },
          // Traction channel #5 (organic): LinkedIn first, Instagram as proof.
          { title: 'Social', link: ROUTES.INTERNAL_GTM_SOCIAL, icon: <MessagesSquareIcon strokeWidth={1} /> },
          // VRG-684 — Affiliates (was "Insiders", VRG-678): both programmes, Refer & earn and
          // Vera Insiders, as two tabs. People → Referrals moved in as part of Refer & earn.
          { title: 'Affiliates', link: ROUTES.INTERNAL_GTM_AFFILIATES, icon: <HandshakeIcon strokeWidth={1} /> },
        ],
      },
      {
        // VRG-744 — "Research" (Spencer, 2026-10-23): what Vera LEARNS about the market, as against
        // GTM above (the channels Vera acts through). Two rows, each true of the label: Market
        // research is the corpus of articles and claims staff collect; Competitor research is what
        // is known about each competitor (directory, pricing, features, posts, search overlap).
        // Directly under GTM because both feed it: SEO terms and articles come out of this group.
        //
        // Each row keeps its own gate, so the heading rides on whichever survives: Market research
        // is `internal_market_intel_view` (platform_admin + platform_manager), Competitor research
        // is `internal_gtm_view` (platform_admin), the audience the competitor record already had.
        section: 'Research',
        rows: [
          // VRG-461 — the market-data corpus: articles and posts staff paste in for
          // research, GTM signal and the Ask Vera market tool (VRG-464). It sat in Operations
          // (an authoring queue beside Support and Announcements) until VRG-744 gave research its
          // own group; it is still a desk someone works, and Research is what it works ON.
          //
          // VRG-738 — "Market research" (was "Content"; Spencer, 2026-10-22), from the one
          // constant the header, palette and breadcrumbs also read. GTM → Content marketing is
          // the content WE publish; this is the research we collect. Route unchanged.
          { title: MARKET_RESEARCH_TITLE, link: ROUTES.INTERNAL_MARKET, icon: <NewspaperIcon strokeWidth={1} /> },
          // VRG-744 — Competitor research, from the one constant the header, palette and
          // breadcrumbs also read. Moved out of GTM → SEO's Competitors tab.
          { title: COMPETITOR_RESEARCH_TITLE, link: ROUTES.INTERNAL_COMPETITORS, icon: <BinocularsIcon strokeWidth={1} /> },
        ],
      },
      {
        // "Accounts" is true of all four rows, which is the bar a group label has to clear:
        // Companies are accounts, Demos are prospective ones, Signups are applications for
        // one, and Subscriptions are the plan an account is on.
        section: 'Accounts',
        rows: [
          // VRG-401 — Signups graduated OUT of Labs (it was `/internal/labs/signups`, a
          // section of the prototype hub) into its own route. Leads the group: ORDERED BY
          // LIFECYCLE, signup -> demo -> customer -> billing (main's ordering, kept on merge).
          // 2026-10-01 — Signups became "People": one table across waitlist,
          // allowlist, pricing and site invites. VRG-727 (2026-10-20) — renamed "Access",
          // at /internal/access ("People" is GTM → Sales' tab of people on the pipeline now).
          // The staff list that held "People" before 2026-10-01 is "Team" below.
          { title: 'Access', link: ROUTES.INTERNAL_ACCESS, icon: <ClipboardListIcon strokeWidth={1} /> },
          // VRG-311 — the AE-facing acquisition demo console, on its own `demo_provision`
          // permission (separate from company creation so it can be revoked alone; see
          // demo.admin.controller.ts's header comment).
          { title: 'Demos', link: ROUTES.INTERNAL_DEMOS, icon: <RadarIcon strokeWidth={1} /> },
          // VRG-401 — Signups graduated OUT of Labs (it was `/internal/labs/signups`, a
          // section of the prototype hub) into its own route. It is not a prototype: it is
          // the live pre-launch gate, the queue a real person works to admit a real company.
          // Labs is where decisions get made, not where operational queues live — and a
          // queue buried two levels deep behind a flask icon is one nobody checks.
          { title: 'Companies', link: ROUTES.INTERNAL_COMPANIES, icon: <StoreIcon strokeWidth={1} /> },
          { title: 'Subscriptions', link: ROUTES.INTERNAL_SUBSCRIPTIONS, icon: <RepeatIcon strokeWidth={1} /> },
        ],
      },
      {
        // ONE word, matching the settings nav's `Personal` / `Company` vocabulary. The group
        // is commerce AND fulfilment, but "Orders & fulfilment" at 11px uppercase with 0.06em
        // tracking is visibly heavier than a single word, and the rows underneath make the
        // fulfilment half self-evident. Each row is true of it: Orders and Invoices are the
        // transaction, Packages and Tags are the goods being transacted.
        section: 'Commerce',
        rows: [
          // ORDERED BY LIFECYCLE, like Accounts: a tag is manufactured, boxed into a
          // package, ordered, then invoiced (main's ordering, kept on merge).
          { title: 'Tags', link: ROUTES.INTERNAL_TAGS, icon: <TagIcon strokeWidth={1} /> },
          { title: 'Packages', link: ROUTES.INTERNAL_PACKAGES, icon: <BoxIcon strokeWidth={1} /> },
          { title: 'Orders', link: ROUTES.INTERNAL_ORDERS, icon: <ShoppingBagIcon strokeWidth={1} /> },
          { title: 'Invoices', link: ROUTES.INTERNAL_INVOICES, icon: <ReceiptTextIcon strokeWidth={1} /> },
        ],
      },
      {
        // True of all four, with no "and" needed: staff/user administration, the platform's
        // integrations, the support queue and the announcements desk are all surfaces Vera
        // staff RUN, as opposed to accounts they manage or goods they ship. The monitoring
        // rows that used to make this label need a conjunction now live in Analytics (first).
        section: 'Operations',
        rows: [
          // "Team" (2026-10-01; was "People", and "Users" before that): this lists VERA
          // EMPLOYEES (gated on `view_platform_supervisors`). "People" moved to the
          // access table in Accounts — the humans Vera lets in — at Spencer's call, and
          // two rows with one name in one sidebar is the thing that settled it. The
          // route moved with the label, /internal/people -> /internal/team, because
          // /internal/people now serves that table. "Staff" was tried once and read stiff.
          { title: 'Team', link: ROUTES.INTERNAL_USERS, icon: <UsersIcon strokeWidth={1} /> },
          { title: 'Integrations', link: ROUTES.INTERNAL_INTEGRATIONS, icon: <BlocksIcon strokeWidth={1} /> },
          // VRG-226 — the cross-company support queue.
          //
          // VRG-721: carries the OPEN count (brand scope — the population the queue opens
          // on, so the badge and the list cannot disagree). Hidden at zero: a "0" pill is
          // a number to read that says nothing, and its absence already means "clear".
          {
            title: 'Support',
            link: ROUTES.INTERNAL_SUPPORT,
            icon: <LifeBuoyIcon strokeWidth={1} />,
            count: openSupportCount ? (openSupportCount > 99 ? '99+' : String(openSupportCount)) : undefined,
          },
          // VRG-221 — the announcement authoring surface, RENAMED from "What's new" to
          // "Announcements" (VRG-401). "What's new" is what the READER sees in the Help
          // popover; this row is the authoring desk behind it, and naming both the same
          // thing made the sidebar look like a second copy of the reader's feed. Sits
          // directly after Support because that is the workflow order: a request arrives in
          // the queue, and the answer to a batch of them is written here.
          { title: 'Announcements', link: ROUTES.INTERNAL_ANNOUNCEMENTS, icon: <MegaphoneIcon strokeWidth={1} /> },
          // VRG-744 — Market research moved to the Research group below GTM.
        ],
      },
      {
        // VRG-672 — about building the PRODUCT rather than running the business; see the IA
        // block above. Labs is the only row for now.
        // VRG-684 — "Tools" (was "Product", VRG-672): Analytics now has a Product row, and a
        // heading of the same word would read as its parent.
        section: 'Tools',
        rows: [
          // VRG-410 — gated through the same table as every other `/internal/*` row.
          // platform_admin-only (`labs.controller.ts`, and `LabsPage` guards itself); the
          // section label rides on it, so a role without Labs gets no empty "Product" heading.
          { title: 'Labs', link: ROUTES.INTERNAL_LABS, icon: <FlaskConical strokeWidth={1} /> },
        ],
      },
      // NO "Docs" ROW (VRG-401). The staff docs site is not gone — it moves behind
      // Help → Documentation, which gains an internal/external toggle (VRG-402). That
      // is where a staffer already goes to look something up, so a second door in the
      // primary nav was a duplicate costing a row of a rail that overflows at 720px.
      //
      // ⚠️ THESE TWO BRANCHES MUST LAND TOGETHER. VRG-401 removes the door; VRG-402
      // builds the replacement. Merging this one alone leaves internal docs reachable
      // only by typing /internal/docs.
    ]

    return groups.flatMap(({ section, rows }) => {
      const visible = rows.filter((row) => canReachInternalPath(row.link))
      // `section` on the first survivor only — it draws the boundary ABOVE its row, so a
      // group with nothing left contributes no heading and no hairline either.
      return visible.map((row, index) => (index === 0 ? { ...row, section } : row))
    })
  }, [canReachInternalPath, openSupportCount])

  // Only persisted in `auto`, the one mode that reads it back on the next load —
  // persisting it from a pinned mode would be writing a value nothing consumes.
  useEffect(() => {
    if (displayMode === 'auto') writeSidebarHoverOpen(hoverOpen)
  }, [hoverOpen, displayMode])

  useEffect(() => {
    writeSidebarDisplayMode(displayMode)
  }, [displayMode])

  // ⌘B / Ctrl-B by default — the same binding Vergence uses, and clear of the ⌘K
  // search palette (VRG-227). The chord comes from the shortcut catalog via the
  // user's stored bindings, so this handler and the hint printed on the Collapse
  // row are one value rather than two literals that can disagree.
  //
  // Toggling from `auto` leaves auto on purpose: the user has just told us
  // explicitly what they want, which is the whole point of the pinned modes.
  //
  // Bound on the window rather than the drawer because the sidebar is rarely what
  // holds focus; the guard below is what keeps a global binding from being rude.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!eventMatchesBinding(event, sidebarToggle)) return
      // ⌘B is "bold" in an editor and a browser binding in a field — never steal it
      // from someone who is typing.
      if (isTypingTarget(event.target)) return

      event.preventDefault()
      setDisplayMode(open ? 'collapsed' : 'expanded')
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, sidebarToggle])

  // Same semantics as the keyboard shortcut: pin to the OPPOSITE of what is
  // currently showing. From `auto` this pins — which is the point, since the
  // control exists to escape hover-reactivity.
  const toggleDisplayMode = () => setDisplayMode(open ? 'collapsed' : 'expanded')

  // Locking FREEZES WHAT YOU CAN SEE rather than jumping to a remembered state:
  // lock while expanded -> 'expanded', lock while collapsed -> 'collapsed'. Anything
  // else would move the sidebar at the moment you asked it to stop moving.
  // Unlocking returns to 'auto', the default.
  const isAuto = displayMode === 'auto'
  const toggleAuto = () => setDisplayMode(isAuto ? (open ? 'expanded' : 'collapsed') : 'auto')

  /*
    THE NAV LIST'S SCROLL BOUNDARIES, AND WHY THEY NEED A MARKER AT ALL.

    The list below is `overflowY: auto`, and the clip edge falls wherever the arithmetic
    puts it — mid-row, in practice. Reported on a collapsed sidebar with "What's new"
    sliced through the middle, which does not read as "this scrolls", it reads as a
    rendering fault.

    THE `hideScrollbar` OPT-OUT IS GONE (VRG-398), and that is the load-bearing change
    here rather than a style preference. It used to sit on this box, which is what the
    paragraph below originally meant by "there is NOTHING on screen that says so": the
    scrollbar was suppressed and the hairlines were the entire affordance.

    That was reported as the sidebar covering the nav. It is not — measured at 1280x720
    with the company menu, this box is 249px tall around 340px of rows, so People is
    sliced and Studio falls outside the clip. Nothing paints over anything; the rows are
    CLIPPED, and both hit-test to themselves once scrolled into view. But a list that
    scrolls with no scrollbar, whose last visible row is cut in half, is indistinguishable
    from a broken one — which is exactly the conclusion it produced.

    So the list now inherits the house scrollbar (`packages/ui/theme/scrollbar.ts`,
    applied globally by `VeraThemeProvider`): thin, neutral rather than brand-tinted, and
    inset off the container edge by a transparent border + `background-clip: content-box`
    so it floats clear of the rail instead of being jammed against it. Every other scroll
    container in the platform already looks like this; the sidebar was the exception.

    It appears ONLY when the list overflows, so nothing changes on a tall viewport — at
    1280x900 the box is 429px around 429px of rows and there is no scrollbar and no
    hairline. The scrolling itself is not optional and cannot be designed away: the
    internal menu runs to 12 rows (672px) and has never fit a 720px viewport at any point
    in this component's history.

    THE HAIRLINES STAY. They are not redundant with the scrollbar — on macOS, which is
    where this is developed, scrollbars are overlays that paint only while scrolling, so
    at rest the hairline is again the only marker. It is also the thing that says WHICH
    boundary has more behind it, which a thumb position only implies.

    So: two stuck-only hairlines, one per boundary, each meaning "there is more THIS
    WAY". Directional rather than permanent — a rule that is always there is chrome that
    carries no state, and the resting sidebar (short menu, nothing clipped) should not
    grow two lines it does not need. Same reasoning, and the same treatment, as
    `StickyFormHeader`'s stuck edge.

    A HAIRLINE, NOT A FADE, and that is settled rather than open. `StickyFormHeader`'s
    docblock records the measurement: the fade it replaced painted a 2.7% delta and
    could not be seen at any height, because spreading the same delta over more pixels
    makes it fainter per pixel, not stronger. That option was removed from this codebase
    on purpose; do not reintroduce it here.

    ON `theme.palette.divider` RATHER THAN `primary[100]`. `StickyFormHeader` uses
    `primary[100]` and measures it at ~12% — but that measurement is against the PAGE
    (dark `rgb(9,9,11)`), and this line sits on the SIDEBAR, which is a raised surface
    (`sidebarSurface`, dark `#18181b` = `rgb(24,24,27)`). The same token there is
    `rgb(39,39,42)` on `rgb(24,24,27)`: 15/255, about 5.9% — half the contrast, on the
    one surface where the edge has to be legible. `divider` is `rgba(255,255,255,0.12)`
    in dark and `rgba(0,0,0,0.12)` in light, so it composites to ~`rgb(52,52,55)` on the
    dark sidebar and ~`rgb(220,220,220)` on the light one: ~11% and ~12%, i.e. the
    intended weight in BOTH modes rather than in one.

    It is also the token already in use for the sidebar's own horizontal rule — the
    `<Divider>` a few rows below the bottom hairline. Matching it is the point: two
    separators a short distance apart at DIFFERENT weights is precisely the
    "reads as a rendering fault" failure `StickyFormHeader` documents for its
    `stuckEdge` opt-out.

    BOTH BORDERS LIVE ON THE SCROLL CONTAINER, not on the blocks either side of it. The
    painted result is identical — a scroll container clips at its padding edge, which is
    inside the border, so the line lands exactly on the boundary — and keeping them here
    means `SidebarSearchTrigger` does not grow a prop describing a sibling's scroll
    state. One component owns the behaviour and both of its edges.

    RE-MEASURED ON MORE THAN SCROLL. A listener alone goes stale the moment the geometry
    changes without a scroll event, which happens constantly here: collapsing the
    sidebar, resizing the window, a permission resolving and adding a row,
    `isInternalStaff` swapping the entire list. So the container and its children are under a
    ResizeObserver, the subtree is under a MutationObserver, and the effect re-runs when
    the rendered menu changes identity.
  */
  const navRef = useRef<HTMLDivElement>(null)
  const [navOverflow, setNavOverflow] = useState({ above: false, below: false })

  useEffect(() => {
    const el = navRef.current
    if (!el) return

    const measure = () => {
      const { scrollTop, clientHeight, scrollHeight } = el
      const above = scrollTop > 0
      // 1px of slack. `scrollHeight` and `clientHeight` are integers rounded from
      // fractional layout, so a list scrolled fully to the end can still report a
      // sub-pixel remainder — which would leave the "more below" line lit at the
      // bottom of the list, saying there is more when there is not.
      const below = scrollTop + clientHeight < scrollHeight - 1
      setNavOverflow(prev =>
        prev.above === above && prev.below === below ? prev : { above, below },
      )
    }

    measure()
    el.addEventListener('scroll', measure, { passive: true })

    // Guarded the same way `AnnouncementCard` guards its observer: both exist in every
    // browser this app supports and neither exists in jsdom, where the bare constructor
    // would throw during a test render rather than merely skipping the re-measure.
    const ro =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    if (ro) {
      // The CONTAINER catches the sidebar collapsing and the viewport resizing; the
      // CHILDREN catch the list itself growing or shrinking. The container is `flex: 1`,
      // so it does not change size when its content does — observing it alone would
      // miss every content change, which is the common case.
      ro.observe(el)
      for (const child of Array.from(el.children)) ro.observe(child)
    }

    const mo =
      typeof MutationObserver === 'undefined' ? null : new MutationObserver(measure)
    // Catches rows being added/removed outright, including the whole list being
    // replaced — at which point the ResizeObserver's targets are stale, but the
    // dependency list below has already re-run this effect and re-observed.
    mo?.observe(el, { childList: true, subtree: true })

    return () => {
      el.removeEventListener('scroll', measure)
      ro?.disconnect()
      mo?.disconnect()
    }
  }, [isInternalStaff, open, INTERNAL_MENU, MAIN_MENU])

  return (
    <Box sx={{ display: 'flex' }}>
      <Drawer
        // onMouseEnter={() => setShowOpenButton(true)}
        // onMouseLeave={() => setShowOpenButton(false)}
        transitionDuration={4000}
        // Always tracked, never always ACTED on: `open` above ignores hoverOpen unless
        // the mode is `auto`, so the pinned modes stay put while the pointer state
        // underneath them remains true (see the note on the state declaration).
        onMouseEnter={() => setHoverOpen(true)}
        onMouseLeave={() => setHoverOpen(false)}
        variant="permanent"
        anchor="left"
        open={open}
        // Per-icon hover motion is applied HERE, once, rather than per nav item: the
        // rail's rows come from six different components and it keys off the class
        // lucide stamps on every glyph, so a row added later is animated by default.
        // Drawer-scoped, so it cannot reach SearchSelect's option list (also
        // ListItemButton) or the Help popover's menu (which portals out). See
        // ../navIconMotion.ts — shared with the Settings left nav, which binds the same
        // table to `.MuiMenuItem-root` instead.
        sx={[
          sidebarIconMotionSx,
          // The selected row's glyph takes the brand gradient. Applied at the same
          // container and keyed off the same `.lucide` class, and an exact complement to
          // the motion above — that table's NOT_INERT excludes `.Mui-selected`, so the
          // row painted here is the one row that never animates.
          selectedNavIconSx(NAV_ROW_LIST_ITEM),
          spaceActive || askExpanded ? { '& .MuiDrawer-paper': { borderTopRightRadius: 0 } } : {},
          askExpanded ? { '& .MuiDrawer-paper': { borderBottomRightRadius: 0 } } : {},
        ]}
      >
        <DrawerHeader>
          {open ? (
            <Stack p={0}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                display="flex"
                justifyContent="space-between"
                sx={{ minHeight: SIDEBAR_HEADER_HEIGHT }}
              >
                {/* TODO: fix grow issue  and make this an SVG for dark / light mode*/}
                <img
                  src={logo}
                  style={{
                    color: theme.palette.primary.contrastText,
                    filter: isDark ? 'brightness(0) invert(1)' : undefined,
                    height: '25px',
                    paddingLeft: '14px',
                    width: '120px',
                    zIndex: 10,
                  }}
                />
                {/* VRG-410 — the "Internal" chip says WHICH CONSOLE you are looking at,
                    so it tracks the nav below it (`isInternalStaff`) rather than the
                    platform_admin role. On `isAdmin` it was absent for every internal
                    staffer who is not an admin, i.e. the chip disagreed with the menu
                    beside it. */}
                {isInternalStaff && (
                  <Button
                    variant="secondary"
                    sx={{fontSize: 12, padding: '4px !important'}}
                    disabled>
                      Internal
                  </Button>
                  // <Typography variant="body4" color="text.secondary">
                  //   Internal
                  // </Typography>
                )}
              </Stack>
            </Stack>
          ) : (
            <Stack>
              <Stack
                direction="row"
                alignItems="center"
                display="flex"
                justifyContent="flex-start"
                sx={{ minHeight: SIDEBAR_HEADER_HEIGHT }}
              >
                {/* // TODO: make internal logo thats purple gradient and make this an SVG icon that uses contrast text color */}
                <img
                  src={vlogo}
                  style={{
                    color: theme.palette.primary.contrastText,
                    filter: isDark ? 'brightness(0) invert(1)' : undefined,
                    height: '25px',
                    width: '100%',
                    left: 0,
                    zIndex: 10,
                  }}
                />
              </Stack>
            </Stack>
          )}
        </DrawerHeader>

        {/* Above the nav list and outside the scroll container: search is global
            chrome, so it stays put while the nav scrolls. Renders for every user
            — internal and company alike — unlike the retired Support accordion,
            which sat in the non-internal branch and so was unreachable for admins. */}
        {/* First row under the logo, above Search — parity with Vergence, which
            renders its collapse toggle at the top of the nav rather than beside
            the brand mark (at 80px the brand row has no space for it). */}
        <SidebarCollapseToggle
          sidebarOpen={open}
          onToggle={toggleDisplayMode}
          isAuto={isAuto}
          onToggleAuto={toggleAuto}
        />

        <SidebarSearchTrigger sidebarOpen={open} onOpenSearch={handleOpenSearch} />

        {/* The drawer paper is already a column flex container, so the nav list
            just takes the leftover space (minHeight:0 lets it actually shrink and
            scroll rather than forcing the column taller).

            This replaced `maxHeight: calc(100vh - HEADER + 132px)`. That reserved
            132px for the bottom block, which really measures 267px — a 135px
            shortfall that let the absolutely-positioned bottom block sit on top of
            the last nav items. It was latent before this epic (it needed a
            viewport under ~751px) but adding the search trigger raised the
            threshold to 801px, so it clipped "Studio" at exactly the 1280x800
            desktop viewport. Measured, not guessed.

            minHeight floor (VRG-248): `minHeight: 0` alone let this box be squeezed
            to ZERO once the fixed siblings (header, collapse row, search, and the
            ~267px bottom block) exceeded the column. Measured at 1280x600 with Text
            size = Extra large: the nav rendered 0px tall and the entire main menu,
            Analytics through Studio, simply vanished while the Help/Settings/account
            block below stayed put. Still no overlap — the flex fix above holds — but
            a nav you cannot see is not an improvement on one that overlaps.

            Two rows is the floor: enough that it reads as a scrollable list rather
            than a sliver, small enough that it only binds on genuinely short
            viewports. The value is in LAYOUT px, which is the correct unit here —
            `zoom` scales the painted result, so a row is 48 layout px at every text
            size and this stays exactly two rows at all four.

            A PARTLY-VISIBLE ROW AT THE FOLD IS THE OVERFLOW, NOT A CLIP BUG. Reported as
            "the last nav row is cut off by 5px" and measured at 1280x800: Tags' painted
            bottom 544 against this box's 539. It reproduces at `scrollTop = 16` and only
            there — sweeping the scroll gives 21px clipped at 0, 13 at 8, 5 at 16, 0 at 24,
            45 at 32. The clip is a continuous function of scroll position, so the 5 is
            where the fold happened to land when it was measured, not an off-by-five.

            Every row is reachable: at maximum scroll the last pill's bottom is 534 against
            this box's 539, and that residual 5 is the last `ListItem`'s own
            SIDEBAR_ROW_GUTTER (4) plus the reserved 1px border below — the same 5, opposite
            sign, which is what makes the two easy to conflate.

            AND THE HEIGHT IS DERIVED, NOT RESERVED. `flex: 1` means this box is handed the
            leftover column space, so no constant can go stale underneath it. Verified by
            arithmetic at three viewports — header + collapse row + search + THIS BOX + the
            footer block, exact to the pixel:

              1280x800   88 + 64 + 73 + 314 + 261 = 800
              1280x640   88 + 64 + 73 + 154 + 261 = 640
              1280x520   88 + 64 + 73 +  95 + 200 = 520   floor binds, footer shrinks

            (Search is 73 and not 56 because it now carries 17px of bottom padding, so its
            pill sits 16px clear of this box's top rule — the same 16px the collapse row
            sits below the logo header's rule. See `SidebarSearchTrigger`. Re-measured here
            rather than adjusted by arithmetic: all three rows above are browser readings.)

            (This box's term is its OUTER contribution, one less than its measured height,
            because of the `mt: '-1px'` below: at 800 it measures 315 and contributes 314.)
            The 520 row is the case that exercises the `minHeight` floor above — it binds, the
            footer compresses 261 -> 200, and nothing overlaps; every row is still reachable
            there. There is no `calc()`, no footer literal, and nothing to re-derive —
            which is the difference between this and the LABS_HEADER_H clip (4a3f0619c) it
            superficially resembles. See also the deleted `SIDEBAR_FOOTER_HEIGHT` note in
            `config/layout.ts`. */}
        <Box
          ref={navRef}
          // Product-tour anchor (working-conventions §42). The nav list is what
          // the "everything hangs off the sidebar" orientation step spotlights.
          // Present on every in-app route, so tours must not use it to decide
          // whether they are relevant here — that is what `scopeAnchor` is for.
          data-tour="sidebar-nav"
          sx={{
            flex: 1,
            minHeight: 2 * SIDEBAR_ROW_HEIGHT,
            overflowY: 'auto',
            // GIVES BACK THE PIXEL THE RESERVED BORDER BELOW TAKES.
            //
            // `borderTop` is always present (transparent when unlit — see below), so this
            // box's content box starts 1px lower than the box itself. The Search row above
            // is a nav row like any other, so the list's first item should sit the same
            // distance from it as any two rows sit from each other — and it sat 1px further.
            // Measured on the internal rail by toggling this one declaration: the first
            // group's boundary rule sat 23px below Search's painted edge where every later
            // boundary sits 22px below its own neighbour; with the margin, 22px everywhere.
            // The company rail's unsectioned list is the same container, so its first row
            // picks the pixel up too — derived from the shared geometry, not measured there.
            // Not perceptible alone, but it is a border that is deliberately not supposed to
            // affect layout, quietly affecting it.
            //
            // Costs nothing below: this box is `flex: 1`, so it absorbs the pixel and the
            // bottom block does not move (measured — Help's row top is identical either way).
            //
            // A negative margin rather than trimming the `<List>`'s own `pt` in `SidebarList`:
            // that list also renders in the bottom block, OUTSIDE this bordered container,
            // where there is no pixel to give back.
            mt: '-1px',
            // AIR BELOW THE LAST ROW, so it does not sit against the rule.
            //
            // The boundary is meant to carry the same 8px on both sides — the bottom block
            // already gives 8 below it (`pt: 1` on the group + the Help row's own `pt: 1`).
            // Above it there was nothing but the last row's own 4px padding, measured at
            // 5px to the rule against 8px on the other side, so the last nav row looked
            // crammed into the divider while Help sat comfortably clear of it. Reported
            // against Usage, which is simply whichever row happens to be last.
            //
            // On the scroll CONTAINER rather than the list, and that is deliberate: padding
            // inside a scrollport is part of the scrollable content, so it appears only
            // once you have scrolled to the end — exactly when the last row meets the rule
            // and the only time the gap is visible at all.
            pb: '3px',
            // See the block comment above `navRef`. Reserved as `1px solid transparent`
            // rather than added on demand, so the box's geometry is identical in all
            // four states and turning an edge on can never nudge a row by a pixel —
            // the same reason `StickyFormHeader` reserves its border.
            borderTop: `1px solid ${
              navOverflow.above ? theme.palette.divider : 'transparent'
            }`,
            // THE BOTTOM EDGE IS PERMANENT, THE TOP EDGE IS NOT, AND THE ASYMMETRY IS THE
            // POINT — they mark different things.
            //
            // The top edge means only "there is more nav above", so it is directional and
            // earns its stuck-only behaviour. The bottom edge is ALSO a structural boundary:
            // it is where the nav list ends and the utility block below begins (Help,
            // reader, account). That boundary exists whether or not anything is clipped.
            //
            // Tying it to `navOverflow.below` meant it vanished exactly when you had
            // scrolled to the end — i.e. whenever you were looking at the LAST group, which
            // is the moment you most need to know where that group stops. Reported against
            // Insights: with no rule above Help, "Analytics · Ask stats · Help · Labs" read
            // as one four-row group, and Help and Labs looked like Insights members.
            //
            // Fixed HERE rather than by restoring the commented-out `<Divider>` above the
            // Help row (a few hundred lines below). That divider would sit 4px under this
            // border, so whenever the nav did overflow there would be two rules 4px apart —
            // the "reads as a rendering fault" failure this file documents for
            // `StickyFormHeader`'s stuck edge. One boundary, one owner, one line in every
            // state: lit always here, and the divider stays commented out.
            //
            // Nothing is lost on the scroll-affordance side: "more below" is still carried
            // by the house scrollbar (VRG-398) and by content being visibly clipped, and the
            // top hairline still marks the one boundary that has no other marker.
            borderBottom: `1px solid ${theme.palette.divider}`,
            transition: 'border-color 0.15s ease',
          }}
        >
          {/* VRG-410 — `isInternalStaff`, NOT `isAdmin`. Which console you get is a
              question about WHO you are, and the answer had been "are you specifically
              platform_admin": every other internal role was handed MAIN_MENU, the BRAND
              nav — Tags, Products, Certificates, Studio — for a company they do not
              belong to, while `InternalRoute` and the post-auth redirect were already
              sending them to /internal/companies. The rows inside INTERNAL_MENU are
              filtered per-permission above, so widening this does not widen access. */}
          {isInternalStaff ? (
            <>
              <SidebarList sidebarOpen={open} showTooltips={displayMode === 'collapsed'} title="Internal" links={INTERNAL_MENU} />
            </>
          ) : (
            <>
              <SidebarList sidebarOpen={open} showTooltips={displayMode === 'collapsed'} title="Manage" links={MAIN_MENU} />
            </>
          )}
        </Box>
       
        {/* Sits at the bottom of the column via the nav list's flex:1 above,
            rather than position:absolute — so it can no longer overlap the nav.
            maxWidth + overflow:hidden still cap it to the sidebar width so the
            user block's name/email truncate with an ellipsis. */}
        {/*
          `pt: 1` IS THE TOP HALF OF THE GROUP'S SPACING, and it lives on the group rather
          than on the Help row for a reason.

          Measured at 1280x720, where the nav above overflows and this block is pinned to
          the bottom of the rail: the distance from the nav list's bottom hairline to
          Help's pill was 4px, while the distance from the last menu row's pill down to
          the divider was 12px. Same group, two boundaries, three times the gap on one
          side — which is what reads as lopsided.

          The 4px above came from Help's own `ListItem pt: 1` and nothing else; every row
          in this rail carries that same 4px, so raising it there would have made the Help
          row a different shape from its neighbours in order to fix a spacing problem that
          belongs to the CONTAINER. The group owns its own boundary, so the 4px that
          closes the gap sits here, and the rows keep their uniform rhythm.

          8px on both sides, not 12: the divider already sits 8px above the reader row
          below it, so 8 is the figure this block is already using between a hairline and
          a pill. It is also the gap between adjacent pills in the nav list above. Going
          to 12 would have matched the group's own Help->Settings interior gap instead,
          but that costs 8px of nav height at exactly the viewport where the nav is
          already short — see the scroll note above.
        */}
        <Box sx={{ background: sidebarSurface(theme), minWidth: '100%', maxWidth: '100%', overflow: 'hidden', pt: 1 }}>
          {/* {isAdmin && ( */}
            <>
              {/* <Divider flexItem /> */}
              <SidebarHelpPopover sidebarOpen={open} onOpenSearch={handleOpenSearch} />
              {/* VRG-672 — skipped when empty (Vera staff since Labs moved out): an empty
                  list still renders its 4px top padding, which would add to the 8px
                  boundary below Help that the comment under this block budgets. */}
              {BOTTOM_MENU.length > 0 && (
                <SidebarList sidebarOpen={open} showTooltips={displayMode === 'collapsed'} title="" links={BOTTOM_MENU} />
              )}
            </>
          {/* )} */}
          {/* `mt: 1` (4px) on top of the last row's own `pb: 1` (4px) is the bottom half
              of the group's 8px boundary spacing — the mirror of the `pt: 1` on this
              block, documented above.

              It USED to read as "makes the gap equal the 16px Labs<->Settings inter-item
              gap ... the bottom list only pads 8px below its last item". Both numbers
              were doubled: this app's spacing base is 4px (`@repo/ui`, working-conventions
              §29), so the inter-item gap is 8px and the list pads 4px, not 16 and 8. The
              arithmetic that justified this line was therefore describing a layout that
              does not exist; the line survives because 4px is right for a different
              reason, not the one written here before. */}
          <Divider flexItem sx={{ mt: 1 }} />
          {isAuthenticated && (
            <>
              <EncodingDeviceSidebarButton sidebarOpen={open} />
              {/* The display-mode control rides in this menu (not the header) because
                  the profile chip is the one affordance present in BOTH states — a
                  control that vanishes when collapsed traps the user. */}
              <SidebarUserBlock
                sidebarOpen={open}
              />
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  )
}
